import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ConflictResolutionModal = ({ 
  isOpen, 
  onClose, 
  conflictItem, 
  conflictingUsers, 
  onResolveConflict,
  currentUserId = '1'
}) => {
  const [resolutionType, setResolutionType] = useState('split'); // 'split' | 'assign' | 'discuss'
  const [splitQuantities, setSplitQuantities] = useState({});
  const [selectedUser, setSelectedUser] = useState('');
  const [discussionMessage, setDiscussionMessage] = useState('');

  if (!isOpen || !conflictItem) return null;

  const totalConflictQuantity = conflictingUsers?.reduce((sum, user) => sum + user?.requestedQuantity, 0);
  const availableQuantity = conflictItem?.quantity;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(price);
  };

  const handleSplitQuantityChange = (userId, quantity) => {
    setSplitQuantities(prev => ({
      ...prev,
      [userId]: Math.max(0, Math.min(quantity, availableQuantity))
    }));
  };

  const getTotalSplitQuantity = () => {
    return Object.values(splitQuantities)?.reduce((sum, qty) => sum + (qty || 0), 0);
  };

  const handleResolve = () => {
    switch (resolutionType) {
      case 'split': onResolveConflict(conflictItem?.id,'split', { quantities: splitQuantities });
        break;
      case 'assign': onResolveConflict(conflictItem?.id,'assign', { userId: selectedUser });
        break;
      case 'discuss': onResolveConflict(conflictItem?.id,'discuss', { message: discussionMessage });
        break;
    }
    onClose();
  };

  const initializeSplitQuantities = () => {
    const equalSplit = Math.floor(availableQuantity / conflictingUsers?.length);
    const remainder = availableQuantity % conflictingUsers?.length;
    
    const quantities = {};
    conflictingUsers?.forEach((user, index) => {
      quantities[user.id] = equalSplit + (index < remainder ? 1 : 0);
    });
    
    setSplitQuantities(quantities);
  };

  React.useEffect(() => {
    if (isOpen && conflictingUsers?.length > 0) {
      initializeSplitQuantities();
    }
  }, [isOpen, conflictingUsers, availableQuantity]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface border border-border rounded-lg shadow-elevation-3 w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">
              Resolver Conflicto
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8"
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Item Info */}
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 mb-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
              <div>
                <h3 className="font-medium text-foreground mb-1">
                  {conflictItem?.name}
                </h3>
                <p className="text-sm text-text-secondary mb-2">
                  {formatPrice(conflictItem?.price)} • Cantidad disponible: {availableQuantity}
                </p>
                <p className="text-sm text-warning">
                  Múltiples usuarios han reclamado este artículo
                </p>
              </div>
            </div>
          </div>

          {/* Conflicting Users */}
          <div className="mb-4">
            <h4 className="font-medium text-foreground mb-2">Usuarios en conflicto:</h4>
            <div className="space-y-2">
              {conflictingUsers?.map((user) => (
                <div key={user?.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                      {user?.name?.charAt(0)}
                    </div>
                    <span className="text-sm text-foreground">{user?.name}</span>
                  </div>
                  <span className="text-sm text-text-secondary">
                    Solicitó: {user?.requestedQuantity}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Resolution Options */}
          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Opciones de resolución:</h4>

            {/* Split Option */}
            <div className="border border-border rounded-lg p-3">
              <label className="flex items-center space-x-2 mb-3">
                <input
                  type="radio"
                  name="resolution"
                  value="split"
                  checked={resolutionType === 'split'}
                  onChange={(e) => setResolutionType(e?.target?.value)}
                  className="text-primary"
                />
                <span className="font-medium text-foreground">Dividir entre usuarios</span>
              </label>
              
              {resolutionType === 'split' && (
                <div className="space-y-2">
                  {conflictingUsers?.map((user) => (
                    <div key={user?.id} className="flex items-center justify-between">
                      <span className="text-sm text-foreground">{user?.name}:</span>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() => handleSplitQuantityChange(user?.id, (splitQuantities?.[user?.id] || 0) - 1)}
                          disabled={(splitQuantities?.[user?.id] || 0) <= 0}
                          iconName="Minus"
                          iconSize={12}
                        />
                        <Input
                          type="number"
                          value={splitQuantities?.[user?.id] || 0}
                          onChange={(e) => handleSplitQuantityChange(user?.id, parseInt(e?.target?.value) || 0)}
                          className="w-16 text-center"
                          min="0"
                          max={availableQuantity}
                        />
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() => handleSplitQuantityChange(user?.id, (splitQuantities?.[user?.id] || 0) + 1)}
                          disabled={getTotalSplitQuantity() >= availableQuantity}
                          iconName="Plus"
                          iconSize={12}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="text-xs text-text-secondary mt-2">
                    Total asignado: {getTotalSplitQuantity()} de {availableQuantity}
                  </div>
                </div>
              )}
            </div>

            {/* Assign to One User Option */}
            <div className="border border-border rounded-lg p-3">
              <label className="flex items-center space-x-2 mb-3">
                <input
                  type="radio"
                  name="resolution"
                  value="assign"
                  checked={resolutionType === 'assign'}
                  onChange={(e) => setResolutionType(e?.target?.value)}
                  className="text-primary"
                />
                <span className="font-medium text-foreground">Asignar a un usuario</span>
              </label>
              
              {resolutionType === 'assign' && (
                <div className="space-y-2">
                  {conflictingUsers?.map((user) => (
                    <label key={user?.id} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="assignUser"
                        value={user?.id}
                        checked={selectedUser === user?.id}
                        onChange={(e) => setSelectedUser(e?.target?.value)}
                        className="text-primary"
                      />
                      <span className="text-sm text-foreground">{user?.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Discussion Option */}
            <div className="border border-border rounded-lg p-3">
              <label className="flex items-center space-x-2 mb-3">
                <input
                  type="radio"
                  name="resolution"
                  value="discuss"
                  checked={resolutionType === 'discuss'}
                  onChange={(e) => setResolutionType(e?.target?.value)}
                  className="text-primary"
                />
                <span className="font-medium text-foreground">Iniciar discusión</span>
              </label>
              
              {resolutionType === 'discuss' && (
                <Input
                  type="text"
                  placeholder="Escribe un mensaje para iniciar la discusión..."
                  value={discussionMessage}
                  onChange={(e) => setDiscussionMessage(e?.target?.value)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex space-x-2">
          <Button
            variant="outline"
            fullWidth
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            fullWidth
            onClick={handleResolve}
            disabled={
              (resolutionType === 'split' && getTotalSplitQuantity() !== availableQuantity) ||
              (resolutionType === 'assign' && !selectedUser) ||
              (resolutionType === 'discuss' && !discussionMessage?.trim())
            }
          >
            Resolver
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConflictResolutionModal;